/**
 * State-surface, status-truthfulness, and selection-scope rules for
 * FmOperationalWorkspaceRoot.
 *
 * These are the rules that stop a descriptor from encoding a defect the
 * renderer would then faithfully paint: a state with nothing to do
 * a workspace with no selection that still carries
 * entity values (.6.19), a blocker an owner cannot act on (.6.20), and a status
 * rail that contradicts the mutation or revision it reports (.6.25).
 */
import {
  exactOperationalKeys as exact,
  failOperationalDescriptor as fail,
  operationalArray as array,
  operationalObject as object,
  operationalText as text,
} from './FmOperationalWorkspaceValidation.shared';

/**
 * A control named by a state surface or a blocker receipt must exist and must
 * stay usable while the state that produced it inhibits the ordinary toolbar —
 * otherwise the named resolving action renders disabled, or the surface offers
 * an unrelated control instead.
 */
export function requireResolvingControl(
  actionIdValue: unknown,
  path: string,
  controls: Record<string, unknown>[],
): void {
  const actionId = text(actionIdValue, path);
  const control = controls.find((item) => String(item.id) === actionId);
  if (!control) fail(path, 'must reference a declared control');
  if (control?.availableWhenUnavailable !== true) {
    fail(path, 'must reference a control declared availableWhenUnavailable');
  }
  if (control?.disabled === true) fail(path, 'must reference a control that is not disabled');
  /* A confirmation dialog inherits the workspace inhibition, so a resolving
     action behind one opens a modal whose confirm can never be pressed — the
     state would have no exit at all. */
  if (control?.confirmationId !== undefined) {
    fail(path, 'must reference a control that resolves directly, not behind a confirmation');
  }
}

export function validateStateScope(
  value: unknown,
  path: string,
  stageIds: ReadonlySet<string>,
  regionIds: ReadonlySet<string>,
): void {
  const scope = object(value, path);
  if (scope.kind === 'workspace') {
    exact(scope, new Set(['kind']), path);
    return;
  }
  if (scope.kind === 'stage') {
    exact(scope, new Set(['kind', 'stageId']), path);
    if (!stageIds.has(text(scope.stageId, `${path}.stageId`))) {
      fail(`${path}.stageId`, 'must reference a declared stage');
    }
    return;
  }
  if (scope.kind === 'region') {
    exact(scope, new Set(['kind', 'regionId']), path);
    if (!regionIds.has(text(scope.regionId, `${path}.regionId`))) {
      fail(`${path}.regionId`, 'must reference a declared region');
    }
    return;
  }
  fail(`${path}.kind`, 'is not registered');
}

/**
 * The typed surface each non-ready state carries. Every one names its own copy
 * and its own resolving control, so no state can render as one grey sentence
 * with nothing to do.
 */
export function validateStateSurface(
  descriptor: Record<string, unknown>,
  controls: Record<string, unknown>[],
  stageIds: ReadonlySet<string>,
  regionIds: ReadonlySet<string>,
): void {
  const state = String(descriptor.state);
  if (state === 'empty') {
    const empty = object(descriptor.empty, 'descriptor.empty');
    exact(empty, new Set(['description', 'primaryActionId']), 'descriptor.empty');
    text(empty.description, 'descriptor.empty.description');
    requireResolvingControl(empty.primaryActionId, 'descriptor.empty.primaryActionId', controls);
    return;
  }
  if (state === 'stale') {
    const stale = object(descriptor.stale, 'descriptor.stale');
    exact(stale, new Set(['name', 'description', 'refreshActionId']), 'descriptor.stale');
    text(stale.name, 'descriptor.stale.name');
    text(stale.description, 'descriptor.stale.description');
    requireResolvingControl(stale.refreshActionId, 'descriptor.stale.refreshActionId', controls);
    return;
  }
  if (state === 'blocked') {
    const blocked = object(descriptor.blocked, 'descriptor.blocked');
    exact(
      blocked,
      new Set(['id', 'name', 'reason', 'resolvingActionId', 'scope']),
      'descriptor.blocked',
    );
    ['id', 'name', 'reason'].forEach((key) => text(blocked[key], `descriptor.blocked.${key}`));
    requireResolvingControl(
      blocked.resolvingActionId,
      'descriptor.blocked.resolvingActionId',
      controls,
    );
    validateStateScope(blocked.scope, 'descriptor.blocked.scope', stageIds, regionIds);
    return;
  }
  if (state !== 'error') return;
  const error = object(descriptor.error, 'descriptor.error');
  exact(
    error,
    new Set(['scopeLabel', 'description', 'retryActionId', 'failedRegionIds']),
    'descriptor.error',
  );
  ['scopeLabel', 'description'].forEach((key) => text(error[key], `descriptor.error.${key}`));
  requireResolvingControl(error.retryActionId, 'descriptor.error.retryActionId', controls);
  const failed = array(error.failedRegionIds, 'descriptor.error.failedRegionIds').map(
    (item, index) => text(item, `descriptor.error.failedRegionIds[${index}]`),
  );
  failed.forEach((regionId, index) => {
    if (!regionIds.has(regionId)) {
      fail(`descriptor.error.failedRegionIds[${index}]`, 'must reference a declared region');
    }
  });
  if (new Set(failed).size !== failed.length) {
    fail('descriptor.error.failedRegionIds', 'must use unique ids');
  }
  /* One failing scope carries one scopeLabel and one retry. Two failed regions
     would paint the first region's sentence onto the second and render the same
     retry control twice. A wider failure is a
     whole-workspace error: an empty list. */
  if (failed.length > 1) {
    fail(
      'descriptor.error.failedRegionIds',
      'must name at most one failing region — a wider failure is a whole-workspace error',
    );
  }
}

/**
 * Truthfulness rules for the one authoritative status rail: the rail is derived
 * from mutation and revision state, and no descriptor may encode a state that
 * contradicts it.
 */
export function validateStatusTruthfulness(descriptor: Record<string, unknown>): void {
  const revisionStatus = String(object(descriptor.revision, 'descriptor.revision').status);
  const mutation = object(descriptor.mutation, 'descriptor.mutation');
  const state = String(descriptor.state);
  if ((state === 'stale') !== (revisionStatus === 'stale')) {
    fail('descriptor.state', "must be 'stale' exactly when revision.status is 'stale'");
  }
  /* A conflicted revision must be owned by the mutation, so the derived rail can
     report it. The converse is NOT required: a mutation may be rejected as
     conflicted while the served revision is still current. */
  if (revisionStatus === 'conflict' && mutation.status !== 'conflict') {
    fail(
      'descriptor.mutation.status',
      "must be 'conflict' while revision.status is 'conflict', so the status rail can report it",
    );
  }
  /* The state lede and the mutation status are different sentences and are shown
     together. An idle mutation renders no status block at all, so its label
     cannot duplicate anything. */
  if (
    mutation.status !== 'idle' &&
    text(descriptor.stateLabel, 'descriptor.stateLabel') === String(mutation.label)
  ) {
    fail(
      'descriptor.stateLabel',
      'must not repeat descriptor.mutation.label — the state lede and the mutation status are different sentences',
    );
  }
}

/** Elements a ready section places in its slots; an unavailable section places none. */
function sectionSlotElements(section: Record<string, unknown>, path: string): Record<string, unknown>[] {
  if (section.slots === undefined) return [];
  return Object.entries(object(section.slots, `${path}.slots`)).flatMap(([slot, value]) =>
    array(value, `${path}.slots.${slot}`).map((element, index) =>
      object(element, `${path}.slots.${slot}[${index}]`),
    ),
  );
}

/**
 * A workspace with no selection may not carry selected-entity values. Figures
 * in a selection-scoped region render their explicit unavailable treatment;
 * selection-scoped facts assert an entity that does not exist
 */
export function validateSelectionScope(selectionValue: unknown, regionsValue: unknown): void {
  if (object(selectionValue, 'descriptor.selection').kind !== 'none') return;
  array(regionsValue, 'descriptor.regions').forEach((regionValue, regionIndex) => {
    const path = `descriptor.regions[${regionIndex}]`;
    const region = object(regionValue, path);
    if (region.scope !== 'selection' || region.kind !== 'section') return;
    const sectionPath = `${path}.section`;
    sectionSlotElements(object(region.section, sectionPath), sectionPath).forEach((element) => {
      if (element.element === 'keyFigures') {
        array(element.figures, `${sectionPath}.figures`).forEach((figureValue, figureIndex) => {
          if (object(figureValue, `${sectionPath}.figures[${figureIndex}]`).value !== null) {
            fail(
              `${sectionPath}.figures[${figureIndex}].value`,
              'must be null while no entity is selected',
            );
          }
        });
      }
      if (element.element === 'facts') {
        fail(`${sectionPath}.facts`, 'must be empty while no entity is selected');
      }
    });
  });
}
